import { Components } from '@ionic/core';
import { IonSpinner } from '@ionic/react';
import {
  DOMAttributes,
  HTMLAttributes,
  ReactElement,
  forwardRef,
  useEffect,
  useState,
} from 'react';
import { mergeRefs } from 'react-merge-refs';
import { useMeasure } from 'react-use';
import { BaseButton, BaseButtonProps } from './base-button';

export interface LoadingButtonProps extends BaseButtonProps {
  loading: boolean;
  setWidthOnInitializationOnly?: boolean;
  spinnerProps?: Partial<Components.IonSpinner> &
    Partial<DOMAttributes<HTMLIonSpinnerElement>> &
    Partial<HTMLAttributes<HTMLIonSpinnerElement>>;
}

const LoadingButton = forwardRef(
  (props: LoadingButtonProps, ref): ReactElement => {
    const {
      children,
      className,
      loading,
      buttonProps,
      spinnerProps,
      setWidthOnInitializationOnly = false,
    } = props;
    const [minWidth, setMinWidth] = useState<number>(0);

    const [measureRef, { width: buttonWidth }] =
      useMeasure<HTMLIonButtonElement>();

    useEffect(() => {
      const max = Number(Math.max(minWidth, buttonWidth).toFixed(0));
      if (
        (max !== minWidth && !setWidthOnInitializationOnly) ||
        (minWidth === 0 && setWidthOnInitializationOnly)
      ) {
        setMinWidth(max);
      }
    }, [minWidth, buttonWidth, setWidthOnInitializationOnly]);

    return (
      <BaseButton
        buttonId={props.buttonId}
        ref={mergeRefs([ref, measureRef])}
        buttonProps={{
          disabled: buttonProps?.disabled || loading,
          ...buttonProps,
          style: {
            ...buttonProps?.style,
            minWidth,
          },
        }}
        className={className}
      >
        {loading && <IonSpinner name="dots" {...spinnerProps} />}
        {!loading && children}
      </BaseButton>
    );
  }
);

LoadingButton.displayName = 'LoadingButton';

export { LoadingButton };
